// The public catalogue from accounts (GET /v1/catalogue) and the pure browse logic: Kind → genre → facets, plus
// search. Tech tags are developer-view only, so they never appear here (docs/PLAN.md, catalogue decision).

import { api, ApiError } from "./api.ts";

export type Kind = "game" | "experience" | "learn" | "example";
export type Input = "keyboard" | "mouse" | "touch" | "gamepad";

export interface Creator {
  id: string;
  name: string;
  url: string | null;
  claimedBy: string | null;
}

export interface Game {
  id: string;
  slug: string;
  kind: Kind;
  genre: string;
  title: string;
  url: string;
  runtime: "web" | "native";
  creator: Creator;
  blurb: string;
  note: string | null;
  art: string | null;
  palette: { top: string; bottom: string };
  facets: { multiplayer: boolean; input: Input[]; prePlay: string[] };
  updatedAt: number;
  /** Threedom's own game (the plaza); accounts wave 4 */
  firstParty?: boolean;
  /** reviewed local-network opt-in, exact origins; accounts wave 4 */
  localNetwork?: string[];
  /** accounts wave 6: a game can be listed before it's out (a waitlist); missing means released */
  status?: "released" | "coming_soon";
  /** expected release, unix seconds, while coming soon */
  releaseAt?: number | null;
  /** when it was released, unix seconds */
  releasedAt?: number | null;
  // ---- wave 11 (catalogue rows, CONTRACTS.md "Wave 11"); all optional, an older accounts sends none of them ----
  themes?: string[];
  mood?: string[];
  style?: string | null;
  session?: "short" | "medium" | "long" | null;
  /** cover image URL or `/path`, used when there's no `art` key */
  cover?: string | null;
  trailer?: Trailer | null;
  listedAt?: number;
  /** null for fingerprint updates: show a plain "Updated" */
  versionLabel?: string | null;
  versionSource?: "hosted" | "manifest" | "feed" | "fingerprint" | null;
  /** unix seconds of the last confirmed update: the Updated badge for 7 days */
  versionUpdatedAt?: number | null;
  /** unclaimed and not first-party: "Is this your game? Claim it or remove it" */
  claimable?: boolean;
  staffPick?: boolean;
  /** `?view=developer` only */
  dev?: { origin?: string; updateDetection?: UpdateDetection | null };
}

export interface Trailer {
  url: string;
  poster: string | null;
  credit: string;
  permission: string;
}

export interface UpdateDetection {
  status: "exact" | "fingerprint" | "unreliable" | "none";
  source: string | null;
  checkedAt: number | null;
}

/** A resolved home row (`GET /v1/catalogue` → `rows`). Personal rows come back empty, for the client to fill or drop. */
export interface Row {
  id: string;
  title: string;
  style: "hero" | "spotlight" | "row" | string;
  rule: { personal?: string; [k: string]: unknown } | null;
  season: { from: number; to: number } | null;
  personal: boolean;
  nominatable?: boolean;
  games: string[];
  /** ids in the row through the new-game boost: badge them New or Updated */
  boosted?: string[];
}

export interface List {
  id: string;
  title: string;
  style: "hero" | "spotlight" | "row" | string;
  games: string[];
}

export interface Catalogue {
  version: string;
  updatedAt: number;
  taxonomy: { kinds: Kind[]; genres: Record<string, string[]>; facets: Record<string, unknown> };
  games: Game[];
  creators: Creator[];
  lists: List[];
  /** wave 11; missing on an older accounts (then `lists` drive the home page) */
  rows?: Row[];
}

let cached: Promise<Catalogue> | null = null;

export function getCatalogue(): Promise<Catalogue> {
  cached ??= api<Catalogue>("accounts", "/catalogue", { auth: false }).catch((e) => {
    cached = null;
    throw e;
  });
  return cached;
}

// ---- Home's slim payload (accounts wave 12: GET /v1/catalogue/home, docs/backend/accounts.md "Wave 12") -----------

/** What Home draws from: catalogue-shaped, so `homeRows`, `withPortals` and `inSeason` read it unchanged. */
export interface HomeCatalogue extends Catalogue {
  /** listed games per kind (all of them, not only the ones the rows show) */
  kindCounts: Partial<Record<Kind, number>>;
}

const KINDS: Kind[] = ["game", "experience", "learn", "example"];
const str = (v: unknown, d = ""): string => (typeof v === "string" ? v : d);

/** One `/home` card as a Game: cards leave out the full record's note, prePlay, creator links and updatedAt. */
export function cardToGame(c: Record<string, unknown>): Game | null {
  if (typeof c.id !== "string" || typeof c.title !== "string") return null;
  const cr = (c.creator ?? {}) as Record<string, unknown>;
  const fa = (c.facets ?? {}) as Record<string, unknown>;
  const pal = (c.palette ?? {}) as Record<string, unknown>;
  const kind = KINDS.includes(c.kind as Kind) ? (c.kind as Kind) : "game";
  return {
    ...(c as object),
    id: c.id,
    slug: str(c.slug, c.id.replace(/^g_/, "")),
    kind,
    genre: str(c.genre, "other"),
    title: c.title,
    url: str(c.url),
    runtime: c.runtime === "native" ? "native" : "web",
    creator: { id: str(cr.id), name: str(cr.name), url: null, claimedBy: null },
    blurb: str(c.blurb),
    note: null,
    art: typeof c.art === "string" ? c.art : null,
    palette: { top: str(pal.top, "#333333"), bottom: str(pal.bottom, "#111111") },
    facets: { multiplayer: fa.multiplayer === true, input: Array.isArray(fa.input) ? (fa.input as Input[]) : [], prePlay: [] },
    updatedAt: 0,
  } as Game;
}

/** The `/home` response as a HomeCatalogue, or null when it isn't one (then Home reads the full catalogue). */
export function homeFromPayload(raw: unknown): HomeCatalogue | null {
  const r = raw as Record<string, unknown> | null;
  if (!r || !Array.isArray(r.rows) || !Array.isArray(r.games)) return null;
  const games = (r.games as unknown[]).map((c) => (c && typeof c === "object" ? cardToGame(c as Record<string, unknown>) : null)).filter((g): g is Game => !!g);
  const kinds = (r.kinds && typeof r.kinds === "object" ? r.kinds : {}) as Record<string, unknown>;
  const kindCounts: Partial<Record<Kind, number>> = {};
  for (const k of KINDS) if (typeof kinds[k] === "number") kindCounts[k] = kinds[k] as number;
  const rows = (r.rows as Row[]).filter((x) => x && typeof x.id === "string" && Array.isArray(x.games));
  return {
    version: str(r.catalogueVersion, str(r.version)),
    updatedAt: typeof r.updatedAt === "number" ? r.updatedAt : 0,
    taxonomy: { kinds: KINDS.filter((k) => (kindCounts[k] ?? 0) > 0), genres: {}, facets: {} },
    games,
    creators: [],
    lists: [],
    rows,
    kindCounts,
  };
}

/** The full catalogue seen as Home's payload (an accounts without `/home`, or the catalogue already loaded). */
export function homeFromCatalogue(cat: Catalogue): HomeCatalogue {
  const kindCounts: Partial<Record<Kind, number>> = {};
  for (const g of cat.games) kindCounts[g.kind] = (kindCounts[g.kind] ?? 0) + 1;
  return { ...cat, kindCounts };
}

let home: Promise<HomeCatalogue> | null = null;

/**
 * Home's data: the slim `/v1/catalogue/home?per_row=12` (25 KB gzip, against the full catalogue's 118 KB). Falls
 * back to the full catalogue when accounts has no `/home` yet, and uses the full one when it's already loaded.
 */
export function getHome(): Promise<HomeCatalogue> {
  if (cached) return cached.then(homeFromCatalogue);
  home ??= api<unknown>("accounts", "/catalogue/home?per_row=12", { auth: false })
    .then(
      (raw) => homeFromPayload(raw) ?? getCatalogue().then(homeFromCatalogue),
      () => getCatalogue().then(homeFromCatalogue),
    )
    .catch((e) => {
      home = null;
      throw e;
    });
  return home;
}

/** Fetch the full catalogue when the browser is idle, so Browse and search open at once after Home. */
export function warmCatalogue(): void {
  const go = () => void getCatalogue().catch(() => {});
  const idle = (globalThis as { requestIdleCallback?: (f: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
  if (idle) idle(go, { timeout: 4000 });
  else setTimeout(go, 1500);
}

/**
 * A creator by id (`cr_…`). The catalogue's list holds only creators with a listed game, so a creator with just a
 * handle (accounts wave 6, `POST /v1/creators`) comes from accounts' single read `GET /v1/catalogue/creators/:id`.
 * `games` are their listed games' ids. null = no such creator.
 */
export async function findCreator(id: string): Promise<{ creator: Creator; games: string[] } | null> {
  const cat = await getCatalogue().catch(() => null);
  const listed = cat?.creators.find((c) => c.id === id);
  if (listed) return { creator: listed, games: cat!.games.filter((g) => g.creator.id === id).map((g) => g.id) };
  if (!/^cr_[a-z0-9][a-z0-9_-]{0,63}$/.test(id)) return null;
  let p = single.get(id);
  if (!p) single.set(id, (p = readCreator(id)));
  return p.catch((e) => {
    single.delete(id);
    throw e;
  });
}

/** Single creator reads, once per page load (a creator page and its assets section both ask). */
const single = new Map<string, Promise<{ creator: Creator; games: string[] } | null>>();

async function readCreator(id: string): Promise<{ creator: Creator; games: string[] } | null> {
  try {
    const r = await api<{ creator?: Partial<Creator>; games?: unknown }>("accounts", `/catalogue/creators/${encodeURIComponent(id)}`, { auth: false });
    const c = r.creator;
    if (!c || typeof c.id !== "string") return null;
    return {
      creator: { id: c.id, name: typeof c.name === "string" && c.name ? c.name : c.id.replace(/^cr_/, ""), url: typeof c.url === "string" ? c.url : null, claimedBy: typeof c.claimedBy === "string" ? c.claimedBy : null },
      games: Array.isArray(r.games) ? r.games.filter((g): g is string => typeof g === "string") : [],
    };
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null; // accounts' "no such creator" (and an old accounts without the read)
    throw e;
  }
}

/** Forget the cached catalogue (after a claim changes `claimedBy`). */
export function refreshCatalogue(): void {
  cached = null;
  home = null;
  single.clear();
}

export const KIND_LABEL: Record<Kind, string> = { game: "Games", experience: "Experiences", learn: "Learn", example: "Examples" };
export const INPUT_LABEL: Record<Input, string> = { keyboard: "Keyboard", mouse: "Mouse", touch: "Touch", gamepad: "Gamepad" };
export const PREPLAY_LABEL: Record<string, string> = {
  ads: "Ads before play",
  cookie_banner: "Cookie banner",
  consent_screen: "Consent screen",
  bot_check: "Bot check",
  long_load: "Long first load",
};

export function label(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1).replace(/[-_]/g, " ");
}

export interface Filter {
  kind?: Kind | null;
  genre?: string | null;
  multiplayer?: boolean;
  input?: Input[];
  /** only games that open straight into play (no ads, banners, bot checks…) */
  noPrePlay?: boolean;
  q?: string;
  /** a home row's id (wave 11): browse within that row's games, in the row's order */
  row?: string | null;
  /** Browse's "Learn" chip (wave 12): courses, examples, demos and portfolios, which "All games" leaves out */
  learn?: boolean;
}

/** Courses, starter examples, tech demos and portfolios: Browse's "Learn" chip, never "All games". */
export function isLearnish(g: Pick<Game, "kind" | "genre">): boolean {
  return g.kind === "learn" || g.kind === "example" || (g.kind === "experience" && (g.genre === "demo" || g.genre === "portfolio"));
}

/** Search tokens; diacritics and case don't matter. */
export function tokens(s: string): string[] {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** 0 = no match. Title hits outrank creator and genre hits, which outrank the blurb. Every query word must match. */
export function searchScore(g: Game, q: string): number {
  const words = tokens(q);
  if (words.length === 0) return 1;
  const title = tokens(g.title);
  const meta = tokens(`${g.creator.name} ${g.genre} ${g.kind}`);
  const blurb = tokens(g.blurb);
  let score = 0;
  for (const w of words) {
    const hit = (list: string[]) => list.some((t) => t.startsWith(w));
    if (title.some((t) => t === w)) score += 10;
    else if (hit(title)) score += 6;
    else if (hit(meta)) score += 3;
    else if (hit(blurb)) score += 1;
    else return 0;
  }
  return score;
}

export function filterGames(games: Game[], f: Filter): Game[] {
  const out = games.filter(
    (g) =>
      (!f.kind || g.kind === f.kind) &&
      (!f.genre || g.genre === f.genre) &&
      (!f.multiplayer || g.facets.multiplayer) &&
      (!f.input?.length || f.input.every((i) => g.facets.input.includes(i))) &&
      (!f.noPrePlay || g.facets.prePlay.length === 0),
  );
  if (!f.q?.trim()) return out;
  return out
    .map((g, i) => ({ g, i, s: searchScore(g, f.q!) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((x) => x.g);
}

/** Genres that have at least one game of this kind, in taxonomy order. */
export function genresFor(cat: Pick<Catalogue, "taxonomy" | "games">, kind: Kind | null | undefined): string[] {
  const present = new Set(cat.games.filter((g) => !kind || g.kind === kind).map((g) => g.genre));
  const order = kind ? (cat.taxonomy.genres[kind] ?? []) : Object.values(cat.taxonomy.genres).flat();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const g of [...order, ...present]) if (present.has(g) && !seen.has(g)) (seen.add(g), out.push(g));
  return out;
}

export function filterFromQuery(params: URLSearchParams): Filter {
  const kind = params.get("kind") as Kind | null;
  return {
    kind: kind && ["game", "experience", "learn", "example"].includes(kind) ? kind : null,
    genre: params.get("genre"),
    multiplayer: params.get("multiplayer") === "1",
    input: (params.get("input") ?? "").split(",").filter((i): i is Input => ["keyboard", "mouse", "touch", "gamepad"].includes(i)),
    noPrePlay: params.get("clean") === "1",
    q: params.get("q") ?? "",
    row: /^[a-z0-9][a-z0-9-]{0,39}$/.test(params.get("row") ?? "") ? params.get("row") : null,
    ...(params.get("learn") === "1" ? { learn: true } : {}),
  };
}

export function filterToQuery(f: Filter): string {
  const p = new URLSearchParams();
  if (f.row) p.set("row", f.row);
  if (f.learn) p.set("learn", "1");
  if (f.kind) p.set("kind", f.kind);
  if (f.genre) p.set("genre", f.genre);
  if (f.multiplayer) p.set("multiplayer", "1");
  if (f.input?.length) p.set("input", f.input.join(","));
  if (f.noPrePlay) p.set("clean", "1");
  if (f.q?.trim()) p.set("q", f.q.trim());
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Cover art the launcher bundles (launcher/assets/featured), by the catalogue's `art` key. */
export function artUrl(art: string | null, variant: "full" | "thumb" | "blur" | "title" = "thumb"): string | null {
  if (!art || !/^[a-z0-9-]+$/.test(art)) return null;
  return `/art/${art}${variant === "full" ? "" : `-${variant}`}${variant === "title" ? ".png" : ".jpg"}`;
}

export function gameById(cat: Catalogue, id: string): Game | undefined {
  return cat.games.find((g) => g.id === id);
}

// ---- coming soon (waitlists, accounts wave 6) ----------------------------------------------------------------------

/** A coming-soon game has no Play anywhere on the site: it offers "Notify me in Threedom" instead. */
export function isComingSoon(g: Pick<Game, "status">): boolean {
  return g.status === "coming_soon";
}

/**
 * The expected date of a coming-soon game in the viewer's zone, or null when there's none to show: no date, a
 * malformed one, or one that has passed while the game still isn't out (then it's just "Coming soon").
 * `releaseAt` is unix seconds. `withTime` adds the time and the zone's short name (the game page).
 */
export function expectedDate(releaseAt: number | null | undefined, opts: { now?: number; timeZone?: string; locale?: string; withTime?: boolean } = {}): string | null {
  if (typeof releaseAt !== "number" || !Number.isFinite(releaseAt) || releaseAt <= 0) return null;
  const ms = releaseAt * 1000;
  if (ms <= (opts.now ?? Date.now())) return null;
  const f: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric", timeZone: opts.timeZone };
  if (opts.withTime) Object.assign(f, { weekday: "short", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return new Intl.DateTimeFormat(opts.locale, f).format(ms);
}

/** "Coming soon" or "Coming 18 Oct 2026". */
export function comingSoonText(g: Pick<Game, "status" | "releaseAt">, opts: Parameters<typeof expectedDate>[1] = {}): string | null {
  if (!isComingSoon(g)) return null;
  const d = expectedDate(g.releaseAt, opts);
  return d ? `Coming ${d}` : "Coming soon";
}
